// src/utils/apiClient.js
import axios from 'axios';
import { auth } from '../firebase';
import config from '../config';
import { tokenManager } from '../auth/TokenManager';

// Deliberately generous: the slowest legitimate calls are AI generation and
// projections, while ordinary reads finish in seconds. The point is only to
// stop a stalled connection from pending forever, which surfaces to the user
// as a spinner that never resolves. Individual calls may pass a shorter value.
const REQUEST_TIMEOUT = 180000;

const api = axios.create({
  baseURL: config.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: REQUEST_TIMEOUT
});

// Add setAuthToken method to update authorization header
const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Enhanced request interceptor with token refresh
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error('Error getting token:', error);
    }
  }
  return config;
});

// Enhanced response interceptor with token refresh retry
api.interceptors.response.use(
    (response) => {
      tokenManager.resetOnActivity();
      return response.data;
    },
    async (error) => {
      // Don't log 422 errors from enrollment-projections endpoint since we handle them gracefully
      const shouldSuppressLog = error.response?.status === 422 && 
                                error.config?.url?.includes('/enrollment-projections');
      
      if (!shouldSuppressLog) {
        console.error(`Error response from: ${error.config.url}`, 
                      { status: error.response?.status });
      }

    const originalRequest = error.config;
    
    // Handle 401 errors with token refresh and retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const user = auth.currentUser;
        if (user) {
          // Force token refresh
          const newToken = await user.getIdToken(true);
          setAuthToken(newToken);
          
          // Retry the original request with new token
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
      
      // Redirect to login if refresh fails
      window.location.href = '/login';
    }
    
    // Enhanced error info. Blueprints are inconsistent about whether the
    // failure text lands in `message` or `error`, so check both before
    // falling back to axios's generic "Request failed with status code N".
    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

    const enhancedError = {
      message: isTimeout
        ? 'The server took too long to respond. Please try again.'
        : error.response?.data?.message || error.response?.data?.error || error.message,
      status: error.response?.status,
      isNetwork: !error.response,
      isTimeout,
      isServer: error.response?.status >= 500
    };

    return Promise.reject(enhancedError);
  }
);

// Create the apiClient object with all methods
const apiClient = {
  ...api,
  setAuthToken
};

// Export the apiClient
export default apiClient;